import types from '../../mutation-types';
import { makeConversationQueryKey, isCacheableConversationQuery } from 'dashboard/helper/conversationQueryKey';
import { cacheFresh } from '../conversationQueryCache';
import ConversationApi from '../../../api/inbox/conversation';
import MessageApi from '../../../api/inbox/message';
import { MESSAGE_STATUS, MESSAGE_TYPE } from 'shared/constants/messages';
import { createPendingMessage } from 'dashboard/helper/commons';
import {
  buildConversationList,
  isOnMentionsView,
  isOnParticipatingView,
  isOnUnattendedView,
  isOnFoldersView,
} from './helpers/actionHelpers';
import messageReadActions from './actions/messageReadActions';
import messageTranslateActions from './actions/messageTranslateActions';
import * as Sentry from '@sentry/vue';
import {
  handleVoiceCallCreated,
  handleVoiceCallUpdated,
  syncConversationCallVisibility,
} from 'dashboard/helper/voice';

export const hasMessageFailedWithExternalError = pendingMessage => {
  // This helper is used to check if the message has failed with an external error.
  // We have two cases
  // 1. Messages that fail from the UI itself (due to large attachments or a failed network):
  //    In this case, the message will have a status of failed but no external error. So we need to create that message again
  // 2. Messages sent from Chatwoot but failed to deliver to the customer for some reason (user blocking or client system down):
  //    In this case, the message will have a status of failed and an external error. So we need to retry that message
  const { content_attributes: contentAttributes, status } = pendingMessage;
  const externalError = contentAttributes?.external_error ?? '';
  return status === MESSAGE_STATUS.FAILED && externalError !== '';
};

// Personal unread state only counts incoming messages, so events for outgoing,
// activity or template messages (e.g. provider delivery receipts) cannot change it.
export const affectsPersonalRead = message =>
  !!message?.conversation?.personal_read_enabled &&
  message.message_type === MESSAGE_TYPE.INCOMING;

const messageRequestIdentity = ({ rootGetters, rootState }) => JSON.stringify([
  rootGetters?.getCurrentAccountId,
  rootGetters?.getCurrentUserID,
  rootState?.conversationQueryCache?.identityEpoch,
]);

const validateListResponse = (data, page) => {
  if (!Array.isArray(data?.payload) || !data?.meta ||
      typeof data.meta !== 'object' || Array.isArray(data.meta) ||
      data.payload.some(chat => !chat || chat.id == null || !chat.meta)) {
    throw new Error('Invalid conversation list response');
  }
  if (data.meta.list_version != null && data.meta.list_version !== 1) {
    throw new Error('Unsupported conversation list version');
  }
  if (data.payload.some(chat => (chat.list_summary === true) !== (data.meta.list_version === 1))) {
    throw new Error('Invalid conversation list format');
  }
  if (data.meta.list_version === 1 && (typeof data.meta.has_more !== 'boolean' ||
      !Number.isSafeInteger(data.meta.page_size) || data.meta.page_size < 1 ||
      (data.meta.has_more && (!Number.isSafeInteger(data.meta.next_page) || data.meta.next_page <= page)))) {
    throw new Error('Invalid conversation pagination');
  }
};

// Own the entire refresh, including staged pages, by one request identity.
const fetchConversationList = async (context, params, filter, fetchData, queryKey = null, refreshThrough = 1) => {
  const { commit, state, rootGetters } = context;
  const identity = () => JSON.stringify([
    rootGetters?.getCurrentAccountId,
    rootGetters?.getCurrentUserID,
  ]);
  commit('BEGIN_LIST_REQUEST');
  const requestId = state.listRequestId;
  if (queryKey) commit('conversationQueryCache/begin', { key: queryKey, requestId }, { root: true });
  const requestIdentity = identity();
  const current = () => state.listRequestId === requestId && identity() === requestIdentity;
  try {
    let data;
    let page = params.page;
    const payload = [];
    const pageIds = {};
    do {
      data = await fetchData({ ...params, page });
      if (!current()) return { status: 'stale' };
      validateListResponse(data, page);
      payload.push(...data.payload);
      pageIds[page] = data.payload.map(chat => chat.id);
      if (refreshThrough <= 1 || page >= refreshThrough || data.meta.has_more === false || !data.payload.length) break;
      // Legacy responses have no next_page. Never let a malformed cursor loop.
      const nextPage = data.meta.next_page ?? page + 1;
      if (!Number.isSafeInteger(nextPage) || nextPage <= page) throw new Error('Invalid conversation pagination');
      page = nextPage;
    } while (true);
    const replace = params.page === 1;
    const meta = data.payload.length ? data.meta : { ...data.meta, has_more: false, next_page: null };
    if (replace) context.dispatch('conversationPage/reset');
    buildConversationList(context, { ...params, page }, { ...data, payload, meta }, filter);
    commit('SET_LIST_RESULT_IDS', { ids: payload.map(chat => chat.id), page: replace ? 1 : page });
    if (queryKey) {
      commit('conversationQueryCache/succeed', { key: queryKey, requestId, page, replace, pageIds,
        ids: payload.map(chat => chat.id), meta }, { root: true });
      commit('PRUNE_LIST_ENTITIES', context.rootState.conversationQueryCache);
    }
    return { status: 'success' };
  } catch (error) {
    if (!current()) return { status: 'stale' };
    if (queryKey) commit('conversationQueryCache/fail', { key: queryKey, requestId }, { root: true });
    if ([401, 403].includes(error.response?.status)) context.dispatch('clearListContext');
    commit('SET_LIST_ERROR', true);
    return { status: 'error' };
  } finally {
    if (current()) commit(types.CLEAR_LIST_LOADING_STATUS);
  }
};

// actions
const actions = {
  getConversation: async ({ commit }, conversationId) => {
    try {
      const response = await ConversationApi.show(conversationId);
      commit(types.UPDATE_CONVERSATION, response.data);
      commit(`contacts/${types.SET_CONTACT_ITEM}`, response.data.meta.sender);
    } catch (error) {
      // Ignore error
    }
  },

  invalidateListRequests({ commit }) {
    commit('INVALIDATE_LIST_REQUEST');
  },

  fetchAllConversations: async (context, { activate = false, refresh = false } = {}) => {
    const params = JSON.parse(JSON.stringify(context.state.conversationFilters));
    if (!params.conversationType && !params.updatedWithin &&
        (typeof window === 'undefined' || window.chatwootConfig?.conversationListSummary !== false)) params.view = 'list_v1';
    const cache = context.rootState?.conversationQueryCache;
    let queryKey = null;
    let refreshThrough = 1;
    if (cache && isCacheableConversationQuery(params)) {
      queryKey = makeConversationQueryKey({ ...cache, accountId: context.rootGetters.getCurrentAccountId,
        userId: context.rootGetters.getCurrentUserID }, params);
      const switched = cache.activeKey !== queryKey;
      if (activate || switched) {
        context.commit('INVALIDATE_LIST_REQUEST');
        context.commit('conversationQueryCache/activate', queryKey, { root: true });
        context.dispatch('conversationPage/reset');
      }
      const entry = cache.queries[queryKey];
      if ((activate || switched) && entry.fetchedAt != null) {
        context.dispatch('conversationStats/set', entry.meta);
        context.dispatch('conversationPage/setCurrentPage', { filter: params.assigneeType, page: entry.page });
        if (!entry.hasMore) context.dispatch('conversationPage/setEndReached', { filter: params.assigneeType });
        if (!refresh && cacheFresh(entry)) return { status: 'success', cached: true };
      }
      params.page = refresh || activate || switched || entry.error ? 1 : entry.nextPage;
      if (params.page == null) return { status: 'success', cached: true };
      // Revalidate the visible range, not just page one. Keep rows and page
      // progress intact until every response succeeds; query activation resets.
      if (params.page === 1 && !activate && !switched) refreshThrough = entry.page || 1;
    } else if (cache) {
      context.commit('conversationQueryCache/activate', null, { root: true });
      if (activate || refresh) {
        params.page = 1;
        context.dispatch('conversationPage/reset');
      }
    }
    return fetchConversationList(context, params, params.assigneeType, async requestParams => {
      const response = await ConversationApi.get(requestParams);
      return response.data.data;
    }, queryKey, refreshThrough);
  },

  invalidateListQueries({ commit }) {
    commit('conversationQueryCache/dirty', null, { root: true });
  },
  clearListContext({ commit, dispatch }) {
    commit('conversationQueryCache/clear', null, { root: true });
    commit(types.EMPTY_ALL_CONVERSATION);
    dispatch('conversationPage/reset');
  },
  resetConversationList({ commit }) {
    commit('INVALIDATE_LIST_REQUEST');
    commit('RESET_LIST_RESULTS');
    commit('conversationQueryCache/activate', null, { root: true });
  },

  fetchFilteredConversations: async (context, input) => {
    if (context.rootState?.conversationQueryCache) context.commit('conversationQueryCache/activate', null, { root: true });
    const params = JSON.parse(JSON.stringify(input));
    return fetchConversationList(context, params, 'appliedFilters', async () => {
      const response = await ConversationApi.filter(params);
      return response.data;
    });
  },

  emptyAllConversations({ commit }) {
    commit(types.EMPTY_ALL_CONVERSATION);
  },

  clearSelectedState({ commit }) {
    commit(types.CLEAR_CURRENT_CHAT_WINDOW);
  },

  fetchPreviousMessages: async (context, data) => {
    const { commit, state } = context;
    const identity = messageRequestIdentity(context);
    try {
      const {
        data: { meta, payload },
      } = await MessageApi.getPreviousMessages(data);
      if (messageRequestIdentity(context) !== identity ||
          !state.allConversations.some(chat => chat.id === data.conversationId)) return false;
      if (meta?.personal_read_state) commit('APPLY_PERSONAL_READ', { id: data.conversationId, ...meta.personal_read_state });
      commit(`conversationMetadata/${types.SET_CONVERSATION_METADATA}`, {
        id: data.conversationId,
        data: meta,
      });
      commit(types.SET_PREVIOUS_CONVERSATIONS, {
        id: data.conversationId,
        data: payload,
      });
      if (!payload.length) {
        commit(types.SET_ALL_MESSAGES_LOADED, data.conversationId);
      }
      return true;
    } catch (error) {
      // Do not mark a failed initial fetch as loaded/read.
      throw error;
    }
  },

  fetchAllAttachments: async ({ commit }, conversationId) => {
    let attachments = [];

    try {
      const { data } = await ConversationApi.getAllAttachments(conversationId);
      attachments = data.payload;
    } catch (error) {
      // in case of error, log the error and continue
      Sentry.setContext('Conversation', {
        id: conversationId,
      });
      Sentry.captureException(error);
    } finally {
      // we run the commit even if the request fails
      // this ensures that the `attachment` variable is always present on chat
      commit(types.SET_ALL_ATTACHMENTS, {
        id: conversationId,
        data: attachments,
      });
    }
  },

  syncActiveConversationMessages: async (
    { commit, state, dispatch, rootGetters },
    { conversationId }
  ) => {
    const accountId = rootGetters?.getCurrentAccountId;
    const { allConversations, syncConversationsMessages } = state;
    const lastMessageId = syncConversationsMessages[conversationId];
    const selectedChat = allConversations.find(
      conversation => conversation.id === conversationId
    );
    if (!selectedChat) return;
    try {
      if (selectedChat.message_activity_enabled || (selectedChat.meta?.channel || selectedChat.channel) === 'Channel::Email') {
        let cursor = Number.isSafeInteger(lastMessageId) ? lastMessageId : 0;
        for (let page = 0; page < 100; page += 1) {
          const { data: { meta, payload } } = await MessageApi.getMessagesSince(conversationId, cursor);
          if (rootGetters?.getCurrentAccountId !== accountId) return;
          if (meta?.personal_read_state) commit('APPLY_PERSONAL_READ', { id: conversationId, ...meta.personal_read_state });
          const current = selectedChat.messages || [];
          const ids = new Set(current.map(message => message.id));
          commit(types.SET_MISSING_MESSAGES, {
            id: conversationId,
            data: [...current, ...payload.filter(message => !ids.has(message.id))].sort((a, b) => {
              const time = Number(a.created_at) - Number(b.created_at);
              if (Number.isFinite(time) && time !== 0) return time;
              return Number.isSafeInteger(a.id) && Number.isSafeInteger(b.id) ? a.id - b.id : 0;
            }),
          });
          if (payload.length < 100) {
            commit(types.SET_LAST_MESSAGE_ID_IN_SYNC_CONVERSATION, { conversationId, messageId: null });
            if (!selectedChat.personal_read_enabled) dispatch('markMessagesRead', { id: conversationId }, { root: true });
            return;
          }
          const next = Math.max(...payload.map(message => message.id));
          if (next <= cursor) throw new Error('Invalid message sync cursor');
          cursor = next;
          commit(types.SET_LAST_MESSAGE_ID_IN_SYNC_CONVERSATION, { conversationId, messageId: cursor });
        }
        throw new Error('Message sync page limit reached; cursor retained');
      }
      const { messages } = selectedChat;
      // Fetch all the messages after the last message id
      const {
        data: { meta, payload },
      } = await MessageApi.getPreviousMessages({
        conversationId,
        after: lastMessageId,
      });
      if (rootGetters?.getCurrentAccountId !== accountId) return;
      if (meta?.personal_read_state) commit('APPLY_PERSONAL_READ', { id: conversationId, ...meta.personal_read_state });
      commit(`conversationMetadata/${types.SET_CONVERSATION_METADATA}`, {
        id: conversationId,
        data: meta,
      });
      // Find the messages that are not already present in the store
      const missingMessages = payload.filter(
        message => !messages.find(item => item.id === message.id)
      );
      selectedChat.messages.push(...missingMessages);
      // Sort the messages by created_at
      const sortedMessages = selectedChat.messages.sort((a, b) => {
        return new Date(a.created_at) - new Date(b.created_at);
      });
      commit(types.SET_MISSING_MESSAGES, {
        id: conversationId,
        data: sortedMessages,
      });
      commit(types.SET_LAST_MESSAGE_ID_IN_SYNC_CONVERSATION, {
        conversationId,
        messageId: null,
      });
      if (!selectedChat.personal_read_enabled) dispatch('markMessagesRead', { id: conversationId }, { root: true });
    } catch (error) {
      // Handle error
    }
  },

  setConversationLastMessageId: async (
    { commit, state },
    { conversationId }
  ) => {
    const { allConversations } = state;
    const selectedChat = allConversations.find(
      conversation => conversation.id === conversationId
    );
    if (!selectedChat) return;
    const { messages } = selectedChat;
    const lastMessage = messages.last();
    if (!lastMessage) return;
    commit(types.SET_LAST_MESSAGE_ID_IN_SYNC_CONVERSATION, {
      conversationId,
      messageId: (selectedChat.message_activity_enabled || (selectedChat.meta?.channel || selectedChat.channel) === 'Channel::Email')
        ? Math.max(0, ...messages.map(message => Number.isSafeInteger(message.id) ? message.id : 0))
        : lastMessage.id,
    });
  },

  async refreshLatestWhatsAppMessages({ commit, state, rootGetters }, conversationId) {
    const accountId = rootGetters?.getCurrentAccountId;
    const chat = state.allConversations.find(c => c.id === conversationId);
    if (!chat?.message_activity_enabled || chat.bcw_messages_loading) return;
    commit('SET_WHATSAPP_MESSAGE_LOAD', { id: conversationId, loading: true, error: false });
    try {
      // A late historical attachment can have the greatest ingestion ID while
      // being weeks older. The initial page must have no before-ID cursor.
      const { data: { meta, payload } } = await MessageApi.getPreviousMessages({ conversationId });
      if (rootGetters?.getCurrentAccountId !== accountId) return;
      if (meta?.personal_read_state) commit('APPLY_PERSONAL_READ', { id: conversationId, ...meta.personal_read_state });
      const current = state.allConversations.find(c => c.id === conversationId);
      if (!current) return;
      const fresh = new Map(payload.map(m => [m.id, m]));
      const merged = (current.messages || []).map(m => {
        const updated = fresh.get(m.id); fresh.delete(m.id); return updated || m;
      });
      commit(types.SET_MISSING_MESSAGES, { id: conversationId, data: [...merged, ...fresh.values()] });
      commit(`conversationMetadata/${types.SET_CONVERSATION_METADATA}`, { id: conversationId, data: meta });
      commit(types.SET_CHAT_DATA_FETCHED, conversationId);
    } catch {
      commit('SET_WHATSAPP_MESSAGE_LOAD', { id: conversationId, error: true });
    } finally {
      commit('SET_WHATSAPP_MESSAGE_LOAD', { id: conversationId, loading: false });
    }
  },

  async refreshEmailHistoryMessages({ commit, state, rootGetters }, conversationId) {
    const accountId = rootGetters.getCurrentAccountId;
    const chat = state.allConversations.find(c => c.id === Number(conversationId));
    if (!chat || (chat.meta?.channel || chat.channel) !== 'Channel::Email') return;
    const initialIds = new Set((chat.messages || []).map(message => message.id));
    const { data: { meta, payload } } = await MessageApi.getPreviousMessages({ conversationId });
    if (rootGetters.getCurrentAccountId !== accountId) return;
    if (meta?.personal_read_state) commit('APPLY_PERSONAL_READ', { id: Number(conversationId), ...meta.personal_read_state });
    const current = state.allConversations.find(c => c.id === Number(conversationId));
    if (!current) return;
    const ids = new Set(payload.map(message => message.id));
    const pending = (current.messages || []).filter(message =>
      (!initialIds.has(message.id) || !Number.isSafeInteger(message.id) || message.status === 'progress') && !ids.has(message.id)
    );
    const messages = [...payload, ...pending].sort((a, b) => Number(a.created_at) - Number(b.created_at));
    // Reload the visible page and reset history pagination. Composer drafts
    // live outside this message collection and are never changed here.
    commit(types.SET_MISSING_MESSAGES, { id: Number(conversationId), data: messages });
    commit(`conversationMetadata/${types.SET_CONVERSATION_METADATA}`, { id: Number(conversationId), data: meta });
    commit(types.CLEAR_ALL_MESSAGES_LOADED, Number(conversationId));
    commit(types.SET_CHAT_DATA_FETCHED, Number(conversationId));
  },

  async setActiveChat(context, { data, after }) {
    const { commit, dispatch, state } = context;
    const identity = messageRequestIdentity(context);
    commit(types.SET_CURRENT_CHAT_WINDOW, data);
    commit(types.CLEAR_ALL_MESSAGES_LOADED, data.id);
    if (data.message_activity_enabled && !after) {
      await dispatch('refreshLatestWhatsAppMessages', data.id);
      return;
    }
    const chat = state.allConversations.find(item => item.id === data.id);
    if (chat && chat.dataFetched === undefined && !chat.initial_messages_loading) {
      commit('SET_INITIAL_MESSAGE_LOAD', { id: data.id, loading: true, error: false });
      try {
        const loaded = await dispatch('fetchPreviousMessages', {
          after,
          // List summaries only carry previews; their history starts empty.
          before: chat.messages?.[0]?.id,
          conversationId: data.id,
        });
        if (loaded && messageRequestIdentity(context) === identity) {
          commit(types.SET_CHAT_DATA_FETCHED, data.id);
        }
      } catch (error) {
        if (messageRequestIdentity(context) === identity) {
          commit('SET_INITIAL_MESSAGE_LOAD', { id: data.id, error: true });
        }
      } finally {
        if (messageRequestIdentity(context) === identity) {
          commit('SET_INITIAL_MESSAGE_LOAD', { id: data.id, loading: false });
        }
      }
    }
  },

  assignAgent: async (
    { dispatch },
    { conversationId, agentId, assigneeType }
  ) => {
    try {
      const response = await ConversationApi.assignAgent({
        conversationId,
        agentId,
        assigneeType,
      });
      dispatch('setCurrentChatAssignee', {
        conversationId,
        assignee: response.data,
        assigneeType,
      });
    } catch (error) {
      // Handle error
    }
  },

  setCurrentChatAssignee(
    { commit },
    { conversationId, assignee, assigneeType }
  ) {
    commit(types.ASSIGN_AGENT, { conversationId, assignee, assigneeType });
  },

  assignTeam: async ({ dispatch }, { conversationId, teamId }) => {
    try {
      const response = await ConversationApi.assignTeam({
        conversationId,
        teamId,
      });
      dispatch('setCurrentChatTeam', { team: response.data, conversationId });
    } catch (error) {
      // Handle error
    }
  },

  setCurrentChatTeam({ commit }, { team, conversationId }) {
    commit(types.ASSIGN_TEAM, { team, conversationId });
  },

  toggleStatus: async (
    { commit },
    { conversationId, status, snoozedUntil = null, customAttributes = null }
  ) => {
    try {
      // Update custom attributes first if provided
      if (customAttributes) {
        await ConversationApi.updateCustomAttributes({
          conversationId,
          customAttributes,
        });
        commit(types.UPDATE_CONVERSATION_CUSTOM_ATTRIBUTES, {
          conversationId,
          customAttributes,
        });
      }

      const {
        data: {
          payload: {
            current_status: updatedStatus,
            snoozed_until: updatedSnoozedUntil,
          } = {},
        } = {},
      } = await ConversationApi.toggleStatus({
        conversationId,
        status,
        snoozedUntil,
      });
      commit(types.CHANGE_CONVERSATION_STATUS, {
        conversationId,
        status: updatedStatus,
        snoozedUntil: updatedSnoozedUntil,
      });
    } catch (error) {
      // Handle error
    }
  },

  createPendingMessageAndSend: async ({ dispatch }, data) => {
    const pendingMessage = createPendingMessage(data);
    dispatch('sendMessageWithData', pendingMessage);
  },

  sendMessageWithData: async ({ commit }, pendingMessage) => {
    const { conversation_id: conversationId, id } = pendingMessage;
    try {
      commit(types.ADD_MESSAGE, {
        ...pendingMessage,
        status: MESSAGE_STATUS.PROGRESS,
      });
      const response = hasMessageFailedWithExternalError(pendingMessage)
        ? await MessageApi.retry(conversationId, id)
        : await MessageApi.create(pendingMessage);
      commit(types.ADD_MESSAGE, {
        ...response.data,
        status: MESSAGE_STATUS.SENT,
      });
      commit(types.ADD_CONVERSATION_ATTACHMENTS, {
        ...response.data,
        status: MESSAGE_STATUS.SENT,
      });
    } catch (error) {
      if (error.response?.status === 409 &&
          ['BCW_WHATSAPP_OFFLINE', 'BCW_WHATSAPP_UNAVAILABLE'].includes(error.response?.data?.code) &&
          !hasMessageFailedWithExternalError(pendingMessage)) {
        // These authenticated server responses precede MessageBuilder.perform.
        // Remove only this optimistic row so retry remains in the retained draft.
        commit('REMOVE_UNSUBMITTED_MESSAGE', { conversationId, id });
        error.notSubmitted = true;
        error.message = error.response.data.error;
        throw error;
      }
      const errorMessage = error.response
        ? error.response.data.error
        : undefined;
      commit(types.ADD_MESSAGE, {
        ...pendingMessage,
        meta: {
          error: errorMessage,
        },
        status: MESSAGE_STATUS.FAILED,
      });
      throw error;
    }
  },

  addMessage({ commit, dispatch, rootGetters }, message) {
    commit(types.ADD_MESSAGE, message);
    if (affectsPersonalRead(message)) dispatch('refreshPersonalReadState', { id: message.conversation_id });
    if (message.message_type === MESSAGE_TYPE.INCOMING) {
      commit(types.SET_CONVERSATION_CAN_REPLY, {
        conversationId: message.conversation_id,
        canReply: true,
      });
      commit(types.ADD_CONVERSATION_ATTACHMENTS, message);
    }
    handleVoiceCallCreated(
      message,
      rootGetters?.getCurrentUserID,
      rootGetters?.getCurrentUserAvailability
    );
  },

  updateMessage({ commit, dispatch, rootGetters }, message) {
    commit(types.ADD_MESSAGE, message);
    if (affectsPersonalRead(message)) dispatch('refreshPersonalReadState', { id: message.conversation_id });
    handleVoiceCallUpdated(
      commit,
      message,
      rootGetters?.getCurrentUserID,
      rootGetters?.getCurrentUserAvailability
    );
  },

  deleteMessage: async function deleteLabels(
    { commit },
    { conversationId, messageId }
  ) {
    try {
      const { data } = await MessageApi.delete(conversationId, messageId);
      commit(types.ADD_MESSAGE, data);
      commit(types.DELETE_CONVERSATION_ATTACHMENTS, data);
    } catch (error) {
      throw new Error(error);
    }
  },

  deleteConversation: async ({ commit, dispatch }, conversationId) => {
    try {
      await ConversationApi.delete(conversationId);
      commit(types.DELETE_CONVERSATION, conversationId);
      dispatch('conversationStats/get', {}, { root: true });
    } catch (error) {
      throw new Error(error);
    }
  },

  addConversation({ commit, state, dispatch, rootState }, conversation) {
    const { currentInbox, appliedFilters } = state;
    const {
      inbox_id: inboxId,
      meta: { sender },
    } = conversation;
    const hasAppliedFilters = !!appliedFilters.length;
    const isMatchingInboxFilter =
      !currentInbox || Number(currentInbox) === inboxId;
    if (
      !hasAppliedFilters &&
      !isOnFoldersView(rootState) &&
      !isOnMentionsView(rootState) &&
      !isOnParticipatingView(rootState) &&
      !isOnUnattendedView(rootState) &&
      isMatchingInboxFilter
    ) {
      commit(types.ADD_CONVERSATION, conversation);
      if (conversation.personal_read_enabled && !conversation.personal_read_user_id) dispatch('refreshPersonalReadState', { id: conversation.id });
      dispatch('contacts/setContact', sender);
    }
  },

  addMentions({ dispatch, rootState }, conversation) {
    if (isOnMentionsView(rootState)) {
      dispatch('updateConversation', conversation);
    }
  },

  addUnattended({ dispatch, rootState }, conversation) {
    if (isOnUnattendedView(rootState)) {
      dispatch('updateConversation', conversation);
    }
  },

  updateConversation({ commit, dispatch, rootGetters }, conversation) {
    const sender = conversation.meta?.sender;

    commit(types.UPDATE_CONVERSATION, conversation);
    if (conversation.personal_read_enabled && !conversation.personal_read_user_id) dispatch('refreshPersonalReadState', { id: conversation.id });
    syncConversationCallVisibility(conversation, rootGetters?.getCurrentUserID);

    dispatch('conversationLabels/setConversationLabel', {
      id: conversation.id,
      data: conversation.labels,
    });

    if (sender) dispatch('contacts/setContact', sender);
  },

  updateConversationLastActivity(
    { commit },
    { conversationId, lastActivityAt }
  ) {
    commit(types.UPDATE_CONVERSATION_LAST_ACTIVITY, {
      lastActivityAt,
      conversationId,
    });
  },

  setChatStatusFilter({ commit }, data) {
    commit(types.CHANGE_CHAT_STATUS_FILTER, data);
  },

  setChatSortFilter({ commit }, data) {
    commit(types.CHANGE_CHAT_SORT_FILTER, data);
  },

  updateAssignee({ commit }, data) {
    commit(types.UPDATE_ASSIGNEE, data);
  },

  updateConversationContact({ commit }, data) {
    if (data.id) {
      commit(`contacts/${types.SET_CONTACT_ITEM}`, data);
    }
    commit(types.UPDATE_CONVERSATION_CONTACT, data);
  },

  setActiveInbox({ commit }, inboxId) {
    commit(types.SET_ACTIVE_INBOX, inboxId);
  },

  muteConversation: async ({ commit }, conversationId) => {
    try {
      await ConversationApi.mute(conversationId);
      commit(types.MUTE_CONVERSATION);
    } catch (error) {
      //
    }
  },

  unmuteConversation: async ({ commit }, conversationId) => {
    try {
      await ConversationApi.unmute(conversationId);
      commit(types.UNMUTE_CONVERSATION);
    } catch (error) {
      //
    }
  },

  sendEmailTranscript: async (_, { conversationId, email }) => {
    await ConversationApi.sendEmailTranscript({ conversationId, email });
  },

  updateCustomAttributes: async (
    { commit },
    { conversationId, customAttributes }
  ) => {
    try {
      const response = await ConversationApi.updateCustomAttributes({
        conversationId,
        customAttributes,
      });
      const { custom_attributes } = response.data;
      commit(types.UPDATE_CONVERSATION_CUSTOM_ATTRIBUTES, {
        conversationId,
        customAttributes: custom_attributes,
      });
    } catch (error) {
      // Handle error
    }
  },

  setConversationFilters({ commit }, data) {
    commit(types.SET_CONVERSATION_FILTERS, data);
  },

  clearConversationFilters({ commit }) {
    commit(types.CLEAR_CONVERSATION_FILTERS);
  },

  setChatListFilters({ commit }, data) {
    commit(types.SET_CHAT_LIST_FILTERS, data);
  },

  updateChatListFilters({ commit }, data) {
    commit(types.UPDATE_CHAT_LIST_FILTERS, data);
  },

  assignPriority: async ({ dispatch }, { conversationId, priority }) => {
    try {
      await ConversationApi.togglePriority({
        conversationId,
        priority,
      });

      dispatch('setCurrentChatPriority', {
        priority,
        conversationId,
      });
    } catch (error) {
      // Handle error
    }
  },

  setCurrentChatPriority({ commit }, { priority, conversationId }) {
    commit(types.ASSIGN_PRIORITY, { priority, conversationId });
  },

  setContextMenuChatId({ commit }, chatId) {
    commit(types.SET_CONTEXT_MENU_CHAT_ID, chatId);
  },

  getInboxCaptainAssistantById: async ({ commit }, conversationId) => {
    try {
      const response = await ConversationApi.getInboxAssistant(conversationId);
      commit(types.SET_INBOX_CAPTAIN_ASSISTANT, response.data);
    } catch (error) {
      // Handle error
    }
  },

  ...messageReadActions,
  ...messageTranslateActions,
};

export default actions;
